import React, { useMemo } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { BaseView, BaseText, BaseSafeFormView } from '@/components/base'
import { ApplePayCheckout } from '@/components/ApplePayCheckout'
import type { ApplePayRedirectData } from '@/types/applePay'

export const ApplePayCheckoutScreen: React.FC = () => {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  // Extract payment data from URL parameters
  const paymentData = useMemo<ApplePayRedirectData | null>(() => {
    const token = searchParams.get('token')
    const secret = searchParams.get('secret')
    const transactionId = searchParams.get('transactionId')
    const orderId = searchParams.get('orderId')
    const amount = searchParams.get('amount')
    const currency = searchParams.get('currency')


    if (!token || !secret || !transactionId || !orderId) {
      console.error('[ApplePayCheckoutScreen] Missing required parameters')
      return null
    }

    return {
      paymentSessionToken: token,
      paymentSessionSecret: secret,
      transactionId,
      orderId,
      amount: amount || '0',
      currency: currency || 'USD',
    }
  }, [searchParams])

  // Handle missing parameters
  if (!paymentData) {
    return (
      <BaseSafeFormView
        flex={1}
        bgColor="background.screen"
        justify="center"
        align="center"
        p={24}
      >
        <BaseView mb={16}>
          <BaseText
            fontSize={20}
            fontFamily="semibold"
            color="label.error"
            textAlign="center"
          >
            {t('common:invalidPaymentLink', 'Invalid Payment Link')}
          </BaseText>
        </BaseView>
        <BaseView mb={24}>
          <BaseText fontSize={14} color="label.secondary" textAlign="center">
            {t(
              'common:invalidPaymentLinkDescription',
              'The payment link is missing required information. Please try again.',
            )}
          </BaseText>
        </BaseView>
        {window.opener && (
          <BaseText
            fontSize={14}
            color="accent.primary"
            textAlign="center"
            style={{ cursor: 'pointer', textDecoration: 'underline' }}
            onClick={() => {
              if (window.opener) {
                window.opener.postMessage(
                  {
                    type: 'APPLE_PAY_ERROR',
                    error: 'Invalid payment link',
                  },
                  '*',
                )
              }
              window.close()
            }}
          >
            {t('common:closeWindow', 'Close window')}
          </BaseText>
        )}
      </BaseSafeFormView>
    )
  }

  return (
    <BaseSafeFormView flex={1} bgColor="background.screen">
      {/* Header */}
      <BaseView
        px={16}
        py={12}
        style={{
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        <BaseText fontSize={18} fontFamily="semibold" color="label.default">
          {t('common:applePay', 'Apple Pay')}
        </BaseText>
      </BaseView>

      {/* Content */}
      <BaseView flex={1}>
        <ApplePayCheckout paymentData={paymentData} />
      </BaseView>

      {/* Footer info */}
      <BaseView
        px={16}
        py={12}
        style={{
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        <BaseText fontSize={12} color="label.tertiary" textAlign="center">
          {t(
            'common:securedByCheckout',
            'Secured by Checkout.com • Your payment information is encrypted',
          )}
        </BaseText>
      </BaseView>
    </BaseSafeFormView>
  )
}

export default ApplePayCheckoutScreen
